import React from 'react';
import { arrayOf, shape, string, number, func } from 'prop-types';
import * as d3 from 'd3';
import ChartWrapper from '@/components/Chart/ChartWrapper';
import Chart from '@/components/Chart';
import { useChartDimensions } from '@/hooks/useChartDimensions';
import { getThemeObj } from '@/helpers/index';

const BarStack = ({
  data,
  hovered,
  selected,
  xAccessor,
  handleSegmentClick,
  handleSegmentOver,
  handleSegmentLeave,
}) => {
  const [chartRef, dimensions] = useChartDimensions();
  const {
    width,
    height,
    marginLeft,
    marginRight,
    marginTop,
    marginBottom,
  } = dimensions;

  const handleClick = (id, theme) => {
    handleSegmentClick(id, theme);
  };

  const handleMouseOver = (id, theme) => {
    handleSegmentOver(id, theme);
  };

  const handleMouseLeave = () => {
    handleSegmentLeave();
  };

  const w = width - marginLeft - marginRight;
  const h = height - marginTop - marginBottom;

  const stack = () => {
    const total = d3.sum(data, (d) => d.value);
    let value = 0;
    // eslint-disable-next-line no-return-assign
    return data
      .sort((a, b) => b.value - a.value)
      .map((d) => ({
        ...d,
        value: d[xAccessor] / total, // width
        startValue: value / total,
        endValue: (value += d[xAccessor]) / total,
      }));
  };

  const xScale = d3
    .scaleLinear()
    .range([0, w]) // px
    .domain([0, 1]);

  const items = stack();

  return (
    <ChartWrapper ref={chartRef} height="38px">
      <Chart name="barstack" dimensions={dimensions}>
        {items.map((d) => {
          const x = xScale(d.startValue);
          const { id, theme } = d;
          const isHovered = hovered === id;
          const isSelected = selected === id;
          return (
            <rect
              data-testid={id}
              key={id}
              x={x}
              y={isHovered || isSelected ? -3 : 0}
              height={isHovered || isSelected ? h + 6 : h}
              width={xScale(d.value)}
              fill={getThemeObj(theme).color.main}
              rx={2}
              style={{
                stroke: 'white',
                strokeWidth: 1,
                cursor: 'pointer',
                transition: 'all .1s ease-in-out',
              }}
              onMouseOver={() => handleMouseOver(id, theme)}
              onMouseLeave={handleMouseLeave}
              onClick={() => handleClick(id, theme)}
            />
          );
        })}
      </Chart>
    </ChartWrapper>
  );
};

BarStack.propTypes = {
  data: arrayOf(
    shape({
      name: string.isRequired,
      value: number.isRequired,
    })
  ).isRequired,
  xAccessor: string,
  handleSegmentOver: func,
  handleSegmentLeave: func,
  handleSegmentClick: func,
  hovered: string.isRequired,
  selected: string.isRequired,
};

BarStack.defaultProps = {
  xAccessor: 'value',
  handleSegmentOver: () => {},
  handleSegmentLeave: () => {},
  handleSegmentClick: () => {},
};

export default BarStack;
